import React from "react";
import { Play, Pause, Sparkles } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useContentI18n } from "@/context/ContentI18n";
import { usePlayer } from "@/context/PlayerContext";
import { SongShareCard } from "@/components/SongShareCard";

const SITE_URL = "https://neox.lk";
const WhatsAppIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.47 14.38c-.29-.15-1.7-.84-1.96-.93-.26-.1-.45-.15-.64.14-.19.29-.74.93-.9 1.12-.17.19-.33.22-.62.07-.29-.15-1.22-.45-2.33-1.44-.86-.77-1.44-1.72-1.61-2.01-.17-.29-.02-.45.13-.6.13-.13.29-.34.44-.51.15-.17.19-.29.29-.48.1-.19.05-.36-.02-.51-.07-.15-.64-1.55-.88-2.12-.23-.56-.47-.48-.64-.49l-.55-.01c-.19 0-.5.07-.76.36-.26.29-1 .98-1 2.38s1.02 2.76 1.17 2.95c.15.19 2.02 3.08 4.9 4.32.68.29 1.22.47 1.63.6.69.22 1.31.19 1.81.12.55-.08 1.7-.69 1.94-1.37.24-.67.24-1.25.17-1.37-.07-.12-.26-.19-.55-.34z"/>
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.46 1.32 4.96L2 22l5.25-1.38c1.45.79 3.08 1.21 4.78 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2zm0 18.02h-.01c-1.52 0-3.01-.41-4.31-1.18l-.31-.18-3.2.84.85-3.12-.2-.32a8.2 8.2 0 0 1-1.26-4.38c0-4.54 3.7-8.23 8.24-8.23 2.2 0 4.27.86 5.83 2.42a8.2 8.2 0 0 1 2.41 5.82c0 4.54-3.7 8.24-8.24 8.24z"/>
  </svg>
);

export const SongOfTheDay = ({ song }) => {
  const { t, fontClass } = useLang();
  const { title: ctitle } = useContentI18n();
  const { current, isPlaying, playIndex, togglePlay } = usePlayer();

  if (!song) return null;
  const active = current && current.id === song.id;

  const h = new Date().getHours();
  const greetKey =
    h >= 5 && h < 12 ? "sotd_g_morning" :
    h >= 12 && h < 17 ? "sotd_g_afternoon" :
    h >= 17 && h < 21 ? "sotd_g_evening" : "sotd_g_night";

  const onPlay = () => {
    if (active) togglePlay();
    else playIndex(0, [song]);
  };

  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-8" data-testid="song-of-the-day">
      <div
        className="rounded-3xl p-5 sm:p-6"
        style={{ background: "linear-gradient(135deg, #1C140E 0%, #3B2A16 55%, #92400E 150%)", border: "2px solid var(--border)" }}
      >
        <div className="flex items-center gap-4 sm:gap-6">
          <div className="relative shrink-0">
            <img
              src={song.cover_url}
              alt={song.title_en}
              className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl object-cover shadow-lg"
            />
            <button
              type="button"
              onClick={onPlay}
              data-testid="sotd-play"
              aria-label="Play song of the day"
              className="absolute -bottom-2 -right-2 grid place-items-center w-12 h-12 rounded-full sk-glow transition-transform hover:scale-110 active:scale-95"
              style={{ background: "var(--player-accent)", color: "var(--player-bg)" }}
            >
              {active && isPlaying
                ? <Pause size={22} fill="currentColor" />
                : <Play size={22} fill="currentColor" style={{ marginLeft: 2 }} />}
            </button>
          </div>

          <div className="min-w-0 flex-1">
            <p className="font-ui text-xs sm:text-sm font-bold uppercase tracking-wide flex items-center gap-1.5" style={{ color: "#F5C97A" }}>
              <Sparkles size={14} /> {t("sotd_title")}
            </p>
            <p className={`${fontClass()} text-xl sm:text-2xl font-bold mt-1 leading-tight truncate`} style={{ color: "#fff" }} data-testid="sotd-title">
              {ctitle(song)}
            </p>
            <p className="font-ui text-sm mt-0.5 truncate" style={{ color: "rgba(255,255,255,0.7)" }}>
              {song.artist}
            </p>
            <p className={`${fontClass()} text-sm sm:text-base font-semibold mt-2`} style={{ color: "#F5C97A" }} data-testid="sotd-greeting">
              {t(greetKey)}
            </p>
          </div>
        </div>
        <SongShareCard title={ctitle(song)} artist={song.artist} greeting={t(greetKey)} />
        <a
          href={`https://wa.me/?text=${encodeURIComponent(`${t("sotd_family_msg")}\n${ctitle(song)} — ${song.artist}\n${t("share_card_listen")}\n${SITE_URL}`)}`}
          target="_blank"
          rel="noreferrer"
          data-testid="sotd-family-send"
          className="mt-3 w-full h-14 rounded-2xl font-ui font-bold text-base flex items-center justify-center gap-2.5 transition-transform active:scale-95 hover:scale-[1.01]"
          style={{ background: "#25D366", color: "#0B141A" }}
        >
          <WhatsAppIcon size={22} /> {t("sotd_family_send")}
        </a>
      </div>
    </section>
  );
};
